// pages/student/MyCourses.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  Button,
  Chip,
  Stack,
  Paper,
  Breadcrumbs,
  Link,
  TextField,
  InputAdornment,
  Fade,
} from "@mui/material";
import {
  Home,
  MenuBook,
  Search,
  PlayCircle,
  WhatsApp,
  School,
  TrendingUp,
  EmojiEvents,
  ArrowForward,
  AccessTime,
  Star,
  People,
} from "@mui/icons-material";
import SideBar from "./components/SideBar";
import { postData } from "../../services/Request";
import "../../styles/Course.css";
import AI from "../../assets/Poster/AI.png";
import MS from "../../assets/Poster/MS.png";
import SD from "../../assets/Poster/SD.png";
import SC from "../../assets/Poster/Cyber-Security.jpeg";

const imageMapping = {
  "Microsoft Dynamics": MS,
  "Software Development": SD,
  "Artificial Intelligence": AI,
  Cybersecurity: SC,
};

const MyCourses = () => {
  const nav = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [userCourses, setUserCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [url, setUrl] = useState("");

  useEffect(() => {
    const page = localStorage.getItem("pageUrl");
    setUrl(page || "");

    const user = localStorage.getItem("user");
    if (!user || user === "null") {
      nav("/");
      return;
    }

    try {
      const data = JSON.parse(user);
      setUserInfo(data);

      postData("usercourses", { userId: data.userId })
        .then((response) => {
          if (response.status === true) {
            setUserCourses(response.courses || []);
          }
        })
        .catch((error) => {
          console.error("Error fetching courses:", error);
        })
        .finally(() => setLoading(false));
    } catch (error) {
      console.error("Error parsing user data:", error);
      nav("/");
    }
  }, [nav]);

  const handleGoWhatsapp = () => {
    window.open("https://chat.whatsapp.com/L0362sTsqwiHrv23KrMscn", "_blank");
  };

  const handleClickButton = (course) => {
    const newPage = `${url}teachecall?courseName=${course}`;
    window.open(newPage, "_blank");
  };

  const handleExploreCourses = () => {
    nav("/courses");
  };

  const filteredCourses = userCourses.filter((course) =>
    course.courseName?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const stats = [
    {
      icon: <MenuBook sx={{ fontSize: 24 }} />,
      label: "Total Enrolled",
      value: userCourses.length,
      color: "#2BAD65",
      bgColor: "rgba(43, 173, 101, 0.08)",
    },
    {
      icon: <TrendingUp sx={{ fontSize: 24 }} />,
      label: "In Progress",
      value: userCourses.filter((c) => c.status === false).length,
      color: "#FFD93D",
      bgColor: "rgba(255, 217, 61, 0.08)",
    },
    {
      icon: <EmojiEvents sx={{ fontSize: 24 }} />,
      label: "Completed",
      value: userCourses.filter((c) => c.status === true).length,
      color: "#A0EF48",
      bgColor: "rgba(160, 239, 72, 0.08)",
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "#F7F8F2",
      }}
    >
      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(247, 248, 242, 0.8)",
            backdropFilter: "blur(8px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress sx={{ color: "#2BAD65" }} />
            <Typography sx={{ mt: 2, color: "rgba(26, 26, 26, 0.5)" }}>
              Loading your courses...
            </Typography>
          </Box>
        </Box>
      )}

      {/* Sidebar */}
      <Box
        sx={{
          width: { xs: 0, md: 280 },
          flexShrink: 0,
          display: { xs: "none", md: "block" },
        }}
      >
        <SideBar />
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            backgroundColor: "#ffffff",
            borderBottom: "1px solid rgba(43, 173, 101, 0.06)",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Breadcrumbs
                separator={
                  <ArrowForward
                    sx={{ fontSize: 14, color: "rgba(26,26,26,0.2)" }}
                  />
                }
                aria-label="breadcrumb"
                sx={{ mb: 1 }}
              >
                <Link
                  underline="hover"
                  color="rgba(26, 26, 26, 0.4)"
                  href="/"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontSize: "0.85rem",
                    "&:hover": {
                      color: "#2BAD65",
                    },
                  }}
                >
                  <Home sx={{ fontSize: 16 }} />
                  Home
                </Link>
                <Typography
                  sx={{
                    color: "#2BAD65",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <MenuBook sx={{ fontSize: 16 }} />
                  My Courses
                </Typography>
              </Breadcrumbs>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#1a1a1a",
                  fontSize: { xs: "1.5rem", md: "2rem" },
                }}
              >
                My Learning
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(26, 26, 26, 0.4)",
                  mt: 0.5,
                }}
              >
                Continue your learning journey from where you left off
              </Typography>
            </Grid>

            <Grid item>
              <Chip
                icon={<School sx={{ fontSize: 16 }} />}
                label={`${userInfo?.firstname || "Student"}`}
                sx={{
                  backgroundColor: "rgba(43, 173, 101, 0.08)",
                  color: "#2BAD65",
                  fontWeight: 600,
                  border: "1px solid rgba(43, 173, 101, 0.1)",
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Stats Section */}
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            borderBottom: "1px solid rgba(43, 173, 101, 0.04)",
          }}
        >
          <Grid container spacing={2}>
            {stats.map((stat, index) => (
              <Grid item xs={4} key={index}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "#ffffff",
                    borderRadius: 2,
                    border: "1px solid rgba(43, 173, 101, 0.04)",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 20px rgba(43, 173, 101, 0.06)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: stat.bgColor,
                      borderRadius: "50%",
                      width: 44,
                      height: 44,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: stat.color,
                      flexShrink: 0,
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#1a1a1a",
                        fontSize: "1.1rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(26, 26, 26, 0.4)",
                        fontSize: "0.7rem",
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Search Section */}
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            backgroundColor: "#ffffff",
            borderBottom: "1px solid rgba(43, 173, 101, 0.04)",
          }}
        >
          <TextField
            fullWidth
            placeholder="Search your courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "rgba(26, 26, 26, 0.3)" }} />
                </InputAdornment>
              ),
              sx: {
                "& fieldset": {
                  borderColor: "rgba(43, 173, 101, 0.1)",
                  borderRadius: 2,
                },
                "&:hover fieldset": {
                  borderColor: "rgba(43, 173, 101, 0.2)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#2BAD65",
                },
              },
            }}
            sx={{
              "& .MuiInputBase-input": {
                py: 1.5,
                color: "#1a1a1a",
                "&::placeholder": {
                  color: "rgba(26, 26, 26, 0.3)",
                },
              },
            }}
          />
        </Box>

        {/* Course Grid */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: { xs: 2, md: 3 },
          }}
        >
          {filteredCourses.length > 0 ? (
            <Grid container spacing={3}>
              {filteredCourses.map((course, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Fade in timeout={400 + index * 100}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#ffffff",
                        borderRadius: 3,
                        border: "1px solid rgba(43, 173, 101, 0.06)",
                        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        overflow: "hidden",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: "0 12px 40px rgba(43, 173, 101, 0.1)",
                          borderColor: course.status ? "#A0EF48" : "#2BAD65",
                        },
                      }}
                    >
                      <Box sx={{ position: "relative" }}>
                        <CardMedia
                          component="img"
                          height="180"
                          image={imageMapping[course.courseName] || AI}
                          alt={course.courseName}
                          sx={{
                            transition: "transform 0.5s ease",
                            "&:hover": {
                              transform: "scale(1.05)",
                            },
                          }}
                        />
                        {course.status && (
                          <Chip
                            label="Completed"
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 12,
                              right: 12,
                              backgroundColor: "rgba(160, 239, 72, 0.9)",
                              color: "#1a1a1a",
                              fontWeight: 700,
                              fontSize: "0.6rem",
                              border: "1px solid rgba(160, 239, 72, 0.3)",
                            }}
                          />
                        )}
                      </Box>

                      <CardContent
                        sx={{
                          p: 2.5,
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: "#1a1a1a",
                            fontSize: "1rem",
                            mb: 1,
                          }}
                        >
                          {course.courseName}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(26, 26, 26, 0.5)",
                            fontSize: "0.8rem",
                            mb: 2,
                            flex: 1,
                          }}
                        >
                          {course.description || "No description available"}
                        </Typography>

                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                          <Chip
                            label={course.courseLevel || "Beginner"}
                            size="small"
                            sx={{
                              backgroundColor: "rgba(43, 173, 101, 0.08)",
                              color: "#2BAD65",
                              fontSize: "0.6rem",
                              height: 22,
                            }}
                          />
                          <Chip
                            label={
                              course.status ? "✅ Completed" : "🔄 In Progress"
                            }
                            size="small"
                            sx={{
                              backgroundColor: course.status
                                ? "rgba(160, 239, 72, 0.12)"
                                : "rgba(255, 217, 61, 0.12)",
                              color: course.status ? "#2BAD65" : "#FFD93D",
                              fontSize: "0.6rem",
                              height: 22,
                            }}
                          />
                        </Stack>

                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={
                            course.status ? <EmojiEvents /> : <PlayCircle />
                          }
                          onClick={() =>
                            course.status
                              ? handleGoWhatsapp()
                              : handleClickButton(course.courseName)
                          }
                          sx={{
                            backgroundColor: course.status
                              ? "#A0EF48"
                              : "#2BAD65",
                            color: course.status ? "#1a1a1a" : "#ffffff",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            py: 1,
                            borderRadius: 2,
                            "&:hover": {
                              backgroundColor: course.status
                                ? "#8EDB40"
                                : "#239A56",
                              transform: "translateY(-2px)",
                              boxShadow: course.status
                                ? "0 4px 20px rgba(160, 239, 72, 0.3)"
                                : "0 4px 20px rgba(43, 173, 101, 0.3)",
                            },
                            transition: "all 0.3s ease",
                          }}
                        >
                          {course.status
                            ? "Get Certificate"
                            : "Continue Learning"}
                        </Button>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                backgroundColor: "#ffffff",
                borderRadius: 3,
                border: "1px solid rgba(43, 173, 101, 0.04)",
              }}
            >
              <MenuBook
                sx={{ fontSize: 64, color: "rgba(26, 26, 26, 0.1)", mb: 2 }}
              />
              <Typography variant="h6" sx={{ color: "rgba(26, 26, 26, 0.3)" }}>
                {searchTerm
                  ? "No matching courses found"
                  : "You haven't enrolled in any course yet"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(26, 26, 26, 0.2)", mt: 1 }}
              >
                {searchTerm
                  ? "Try searching with a different keyword"
                  : "Explore our courses and start learning today"}
              </Typography>
              <Button
                variant="contained"
                onClick={handleExploreCourses}
                startIcon={<MenuBook />}
                sx={{
                  mt: 3,
                  backgroundColor: "#2BAD65",
                  color: "#ffffff",
                  fontWeight: 600,
                  px: 4,
                  py: 1.2,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "#239A56",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 20px rgba(43, 173, 101, 0.3)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Explore Courses
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MyCourses;
